import { Link, useParams } from "react-router-dom";
import Seo, { SITE_URL } from "@/components/base/Seo";
import SiteLayout from "@/components/feature/SiteLayout";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import ArticleCard from "@/components/feature/ArticleCard";
import {
  getArticleById,
  hotArticles,
  relatedArticles,
} from "@/mocks/articles";

export default function ArticleDetail() {
  const { id } = useParams<{ id: string }>();
  const article = id ? getArticleById(id) : undefined;

  if (!article) {
    return (
      <SiteLayout>
        <Seo
          title="内容不存在 | 看料88 往期存档"
          description="你访问的往期内容不存在或已调整，可返回往期存档继续浏览其他话题回顾。"
          keywords="看料88,往期存档,内容不存在"
          path="/article"
        />
        <section className="bg-background-50">
          <div className="mx-auto flex w-full flex-col items-center px-4 py-24 text-center md:px-8">
            <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-200 text-3xl text-foreground-500">
              <i className="ri-emotion-sad-line" />
            </span>
            <h1 className="mt-5 font-heading text-2xl font-bold text-foreground-950">没有找到这篇存档</h1>
            <p className="mt-3 max-w-md text-sm text-foreground-600">
              这篇内容可能已被重新整理或链接有误，你可以回到往期存档，按栏目重新查找。
            </p>
            <Link
              to="/archive"
              className="mt-6 whitespace-nowrap rounded-full bg-primary-500 px-6 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
            >
              返回往期存档
            </Link>
          </div>
        </section>
      </SiteLayout>
    );
  }

  const related = relatedArticles(article, 3);
  const hot = hotArticles(5);
  const articleUrl = `${SITE_URL}/article/${article.id}`;
  const toc = article.body
    .map((block, index) => ({ block, index }))
    .filter((item) => item.block.type === "h2");

  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "BlogPosting",
      headline: article.title,
      description: article.summary,
      datePublished: article.date,
      dateModified: article.date,
      inLanguage: "zh-CN",
      articleSection: article.category,
      keywords: article.tags.join(","),
      image: article.cover,
      mainEntityOfPage: articleUrl,
      author: { "@type": "Organization", name: "看料88" },
      publisher: { "@type": "Organization", name: "看料88" },
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "首页", item: `${SITE_URL}/` },
        { "@type": "ListItem", position: 2, name: "往期存档", item: `${SITE_URL}/archive` },
        { "@type": "ListItem", position: 3, name: article.title, item: articleUrl },
      ],
    },
  ];

  return (
    <SiteLayout>
      <Seo
        title={`${article.title} | 看料88 往期存档`}
        description={article.summary}
        keywords={`${article.tags.join(",")},${article.category},看料88往期存档`}
        path={`/article/${article.id}`}
        jsonLd={jsonLd}
      />

      <article className="bg-background-50">
        <div className="border-b border-background-200 bg-background-100">
          <div className="mx-auto w-full px-4 py-8 md:px-8 md:py-10">
            <Breadcrumbs
              items={[
                { label: "首页", to: "/" },
                { label: "往期存档", to: "/archive" },
                { label: article.category, to: `/archive?cat=${article.categoryKey}` },
                { label: article.title },
              ]}
            />
            <div className="mt-4 flex flex-wrap items-center gap-3 text-xs text-foreground-500">
              <span className="rounded-full bg-primary-50 px-2.5 py-0.5 font-medium text-primary-700">
                {article.category}
              </span>
              <time dateTime={article.date} className="flex items-center gap-1">
                <i className="ri-calendar-line" />
                {article.date}
              </time>
              <span className="flex items-center gap-1">
                <i className="ri-time-line" />
                约 {article.readingTime} 分钟
              </span>
              <span className="flex items-center gap-1 text-primary-600">
                <i className="ri-fire-line" />
                热度 {article.heat}
              </span>
            </div>
            <h1 className="mt-4 max-w-4xl font-heading text-2xl font-black leading-snug text-foreground-950 md:text-4xl">
              {article.title}
            </h1>
            <p className="mt-4 max-w-3xl text-sm leading-relaxed text-foreground-600 md:text-base">
              {article.summary}
            </p>
          </div>
        </div>

        <div className="mx-auto w-full px-4 py-10 md:px-8 md:py-14">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-12">
            <div className="lg:col-span-8">
              <div className="h-56 w-full overflow-hidden rounded-lg bg-background-200 md:h-96">
                <img
                  src={article.cover}
                  alt={`${article.title} 配图`}
                  title={`${article.title} - 看料88 往期存档`}
                  className="h-full w-full object-cover object-top"
                />
              </div>

              <div className="mt-8">
                {article.body.map((block, index) => {
                  if (block.type === "h2") {
                    return (
                      <h2
                        key={`${block.type}-${index}`}
                        id={`section-${index}`}
                        className="mt-9 scroll-mt-28 font-heading text-xl font-bold text-foreground-950 md:text-2xl"
                      >
                        {block.text}
                      </h2>
                    );
                  }
                  if (block.type === "h3") {
                    return (
                      <h3
                        key={`${block.type}-${index}`}
                        className="mt-6 font-heading text-lg font-semibold text-foreground-900"
                      >
                        {block.text}
                      </h3>
                    );
                  }
                  if (block.type === "quote") {
                    return (
                      <blockquote
                        key={`${block.type}-${index}`}
                        className="mt-6 border-l-2 border-primary-400 bg-background-100 py-3 pl-4 pr-3 font-heading text-base italic text-foreground-800"
                      >
                        {block.text}
                      </blockquote>
                    );
                  }
                  return (
                    <p key={`${block.type}-${index}`} className="mt-4 text-sm leading-7 text-foreground-700 md:text-base md:leading-8">
                      {block.text}
                    </p>
                  );
                })}
              </div>

              <div className="mt-10 flex flex-wrap items-center gap-2 border-t border-background-200 pt-6">
                <span className="text-xs text-foreground-500">话题标签</span>
                {article.tags.map((tag) => (
                  <Link
                    key={tag}
                    to={`/archive?q=${encodeURIComponent(tag)}`}
                    className="rounded-full bg-secondary-100 px-3 py-1 text-xs text-secondary-800 transition-colors hover:bg-secondary-200"
                  >
                    #{tag}
                  </Link>
                ))}
              </div>
            </div>

            <aside className="lg:col-span-4">
              <div className="flex flex-col gap-6 lg:sticky lg:top-28">
                {toc.length > 0 && (
                  <nav aria-label="本文目录" className="rounded-lg border border-background-200 bg-background-100 p-5">
                    <h2 className="flex items-center gap-2 font-heading text-base font-bold text-foreground-950">
                      <i className="ri-list-check-2 text-primary-500" />
                      本文目录
                    </h2>
                    <ul className="mt-3 flex flex-col gap-2.5 text-sm">
                      {toc.map((item) => (
                        <li key={item.index}>
                          <a
                            href={`#section-${item.index}`}
                            className="block border-l-2 border-background-300 pl-3 text-foreground-600 transition-colors hover:border-primary-400 hover:text-primary-600"
                          >
                            {item.block.text}
                          </a>
                        </li>
                      ))}
                    </ul>
                  </nav>
                )}

                <div className="rounded-lg border border-background-200 bg-background-100 p-5">
                  <h2 className="flex items-center gap-2 font-heading text-base font-bold text-foreground-950">
                    <i className="ri-fire-fill text-primary-500" />
                    热榜回顾
                  </h2>
                  <ol className="mt-3 flex flex-col divide-y divide-background-200">
                    {hot.map((item, index) => (
                      <li key={item.id}>
                        <Link
                          to={`/article/${item.id}`}
                          className="flex items-start gap-3 py-3 text-sm text-foreground-800 transition-colors hover:text-primary-600"
                        >
                          <span
                            className={`flex h-5 w-5 shrink-0 items-center justify-center rounded text-[11px] font-bold ${
                              index < 3 ? "bg-primary-500 text-background-50" : "bg-background-200 text-foreground-600"
                            }`}
                          >
                            {index + 1}
                          </span>
                          <span className="leading-snug">{item.title}</span>
                        </Link>
                      </li>
                    ))}
                  </ol>
                </div>

                <div className="rounded-lg bg-accent-100 p-5">
                  <h2 className="font-heading text-base font-bold text-accent-900">想看更多旧料？</h2>
                  <p className="mt-2 text-sm leading-relaxed text-accent-900/80">
                    进入往期存档，可按栏目与关键词检索全部历史话题复盘。
                  </p>
                  <Link
                    to="/archive"
                    className="mt-3 inline-block whitespace-nowrap rounded-full bg-accent-500 px-4 py-2 text-sm font-medium text-foreground-950 transition-colors hover:bg-accent-400"
                  >
                    浏览往期存档
                  </Link>
                </div>
              </div>
            </aside>
          </div>

          {related.length > 0 && (
            <section aria-labelledby="related-heading" className="mt-14 border-t border-background-200 pt-10">
              <h2 id="related-heading" className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
                同栏目相关回顾
              </h2>
              <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
                {related.map((item) => (
                  <ArticleCard key={item.id} article={item} />
                ))}
              </div>
              <div className="mt-8 flex justify-center">
                <Link
                  to={`/archive?cat=${article.categoryKey}`}
                  className="whitespace-nowrap rounded-full border border-background-300 px-6 py-2.5 text-sm font-medium text-foreground-800 transition-colors hover:border-primary-300 hover:text-primary-600"
                >
                  查看更多「{article.category}」内容
                </Link>
              </div>
            </section>
          )}
        </div>
      </article>
    </SiteLayout>
  );
}